import IconButton from '@/components/common/IconButton';
import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Detail } from '@/types/detail';
import { ConfigurationType } from '@/types/metadata/metadata';
import { findSubroutine } from '@/util/application';
import { boundReference, padToArity } from '@/util/arity';
import { isEmptyValue } from '@/util/typeGuards';
import { argumentsUtil } from '@/util/validation/arguments';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { ParamProps } from './ParamRenderer';
import ParamStringInteger from './ParamStringInteger';

const ParamArguments: FC<ParamProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();
  if (!props.parentId || !metadata) return <></>;

  const values = argumentsUtil().getValue(props.configuration, props.initialValue) || [];

  const chosenSubroutineId = boundReference(props.configuration, props.initialValue as Detail);
  if (!chosenSubroutineId) return <></>;

  const subArgs = findSubroutine(application.subroutines, chosenSubroutineId)?.args;

  return (
    <Card variant='outlined'>
      <Stack gap={1} sx={{ minHeight: '4rem', p: 1 }}>
        <List subheader={props.configuration.label} />
        {!subArgs || !subArgs.length ? (
          <Typography variant='body1' fontStyle='italic' color='text.disabled' p={2} mt={-2}>
            No {props.configuration.label.toLocaleLowerCase()} available
          </Typography>
        ) : (
          subArgs.map((arg, index) => {
            const argType = arg?.type;
            if (!argType) {
              return (
                <Box key={`node.${props.nodeId}.args.${index}`} sx={{ display: 'flex', gap: 1, pl: 2 }}>
                  {props.configuration.required && <Typography variant='body1'>*</Typography>}
                  <Typography variant='caption'>Argument {index + 1}</Typography>
                </Box>
              );
            }
            return (
              <ParamStringInteger
                key={`node.${props.nodeId}.args.${index}`}
                {...props}
                configuration={{
                  type: argType as ConfigurationType,
                  subtype: argType as ConfigurationType,
                  value: '',
                  label: arg?.label || `Argument ${index + 1}`,
                  required: props.configuration.required
                }}
                initialValue={values[index]}
                onChange={(...args) => {
                  const newInitialValue = [...values];
                  args.forEach(([_, newValue]) => (newInitialValue[index] = newValue as Detail));
                  props.onChange([props.configuration.value, padToArity(newInitialValue, subArgs.length)]);
                }}
              />
            );
          })
        )}
        {values.map((value, index) => {
          if (!subArgs || !subArgs[index]) {
            return (
              <Box
                key={`node.${props.nodeId}.args.${index}`}
                sx={{ display: 'flex', color: 'red', alignItems: 'center', gap: 1, pl: 2 }}
              >
                {props.configuration.required && <Typography variant='body1'>*</Typography>}
                <Typography variant='caption'>
                  Argument {index + 1} - {(value?.name ?? isEmptyValue(value)) ? 'empty' : value?.toString()}
                </Typography>
                <IconButton onClick={() => props.onChange([props.configuration.value, values.toSpliced(index, 1)])}>
                  <CloseIcon />
                </IconButton>
              </Box>
            );
          }
        })}
      </Stack>
    </Card>
  );
};

export default ParamArguments;
